import React, { useEffect, useRef } from 'react';
import { Button } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useExportImage } from '../hooks/useExportImage';
import FourPillars from '../components/FourPillars';
import type { DaYunWithAge } from '../utils';
import QRCode from './../assets/0328.jpg';
import './Report.css';

interface ReportProps {
    fourPillars: { year: string; month: string; day: string; hour: string };
    daYun: DaYunWithAge[];
    astrolabe: any;
    aiAnswer: string;
    generating?: boolean;
    generationError?: string;
    isLocked?: boolean;  // 新增：是否锁定
    // reportId?: string;   // 暂时不用
    onBack: () => void;
    onUnlockSuccess?: (fullContent: string) => void;  // 新增：解锁成功回调
}

const Report: React.FC<ReportProps> = ({ fourPillars, daYun, aiAnswer, onBack, generating = false, generationError }) => {
    const donateQrUrl = (import.meta.env.VITE_DONATE_QR_URL as string | undefined)?.trim() || QRCode;
    // 检测是否为移动端
    // 创建AI报告区域的引用
    const aiReportRef = useRef<HTMLDivElement>(null);
    const { exportImage } = useExportImage('分析图谱深度解析报告');

    // ===== 付费墙状态 =====
    // 暂时注释，不使用解锁功能
    // const [isLocked, setIsLocked] = useState(initialIsLocked);
    // const [unlockCode, setUnlockCode] = useState('');

    // 创建解锁粒子效果
    // 暂时注释，不使用解锁功能
    /*
    const createUnlockParticles = () => {
        const colors = ['#d4af6a', '#b8935d', '#f0e6d2', '#ffd700'];
        const particleCount = 30;
        
        for (let i = 0; i < particleCount; i++) {
            const particle = document.createElement('div');
            particle.className = 'unlock-particle';
            particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
            particle.style.left = `${window.innerWidth / 2}px`;
            particle.style.top = `${window.innerHeight / 2}px`;
            
            const angle = (Math.PI * 2 * i) / particleCount;
            const velocity = 100 + Math.random() * 100;
            const tx = Math.cos(angle) * velocity;
            const ty = Math.sin(angle) * velocity;
            
            particle.style.setProperty('--tx', `${tx}px`);
            particle.style.setProperty('--ty', `${ty}px`);
            
            document.body.appendChild(particle);
            
            setTimeout(() => {
                document.body.removeChild(particle);
            }, 1000);
        }
    };
    
    // 验证解锁码
    const handleVerifyUnlock = async () => {
        if (!unlockCode.trim()) {
            message.warning('请输入邀请码');
            return;
        }
        
        if (!reportId) {
            message.error('报告ID不存在，无法解锁');
            return;
        }
        
        try {
            const hide = message.loading('正在验证解锁...', 0);
            
            // 调用解锁 API
            const resp = await fetch('/api/report', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'unlock',
                    code: unlockCode.trim(),
                    id: reportId
                })
            });
            
            const data = await resp.json();
            hide();
            
            if (resp.ok) {
                // 触发粒子效果
                createUnlockParticles();
                
                // 添加付费墙淡出动画
                const paywall = document.querySelector('.report-paywall');
                if (paywall) {
                    paywall.classList.add('paywall-fading');
                }
                
                // 解锁成功，重新获取完整报告内容
                message.success({
                    content: (
                        <span className="unlock-success-badge">
                            ✨ 解锁成功！正在加载完整报告...
                        </span>
                    ),
                    duration: 2
                });
                
                // 等待动画完成后加载内容
                setTimeout(async () => {
                    try {
                        const reportResp = await fetch(`/api/report?id=${reportId}`);
                        if (reportResp.ok) {
                            const reportData = await reportResp.json();
                            
                            // 更新本地状态
                            setIsLocked(false);
                            
                            // 通知父组件更新内容
                            if (onUnlockSuccess && reportData.content) {
                                onUnlockSuccess(reportData.content);
                            }
                            
                            // 添加内容展开动画
                            setTimeout(() => {
                                const content = document.querySelector('.markdown-content');
                                if (content) {
                                    content.classList.add('unlock-expanding');
                                }
                            }, 100);
                            
                            message.success('完整报告已加载！', 2);
                        } else {
                            throw new Error('获取报告失败');
                        }
                    } catch (error) {
                        console.error('加载完整报告失败:', error);
                        message.error('加载完整报告失败，请刷新页面');
                        // 即使加载失败，也解锁付费墙
                        setIsLocked(false);
                    }
                }, 500);
            } else {
                message.error(data.error || '验证失败');
            }
        } catch (error) {
            console.error('验证解锁码失败:', error);
            message.error('验证服务异常');
        }
    };
    */

    // 组件加载后滚动到AI报告区域
    useEffect(() => {
        const timer = setTimeout(() => {
            if (aiReportRef.current) {
                aiReportRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }, 300); // 等待页面渲染完成

        return () => clearTimeout(timer);
    }, []);

    // 导出为图片
    const handleExportImage = () => {
        exportImage('.report-right');
    };

    const hasAiContent = Boolean(aiAnswer && aiAnswer.trim().length > 0);

    return (
        <div className="report-page">
            <div className="report-container">
                {/* 左侧：行为模型与关系结构分析图谱 */}
                <div className="report-left">
                    <div className="report-header">
                        <Button type="link" onClick={onBack} disabled={generating} style={{ padding: 0, fontSize: '16px' }}>
                            ← 返回
                        </Button>
                    </div>

                    <div className="chart-section">
                        <h3 className="section-title">行为模型排盘</h3>
                        <FourPillars {...fourPillars} />
                    </div>

                    <div className="chart-section">
                        <h3 className="section-title">阶段趋势流转</h3>
                        <div className="dayun-grid">
                            {daYun.map((yun, index) => (
                                <div key={index} className="dayun-item">
                                    <div className="dayun-age">{yun.startAge}-{yun.endAge}岁</div>
                                    <div className="dayun-ganzhi">{yun.ganZhi}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* {astrolabe && (
                        <div className="chart-section">
                            <h3 className="section-title">关系结构分析图谱</h3>
                            <ZiWeiChart data={astrolabe} />
                        </div>
                    )} */}
                </div>

                {/* 右侧：AI 深度解析报告 */}
                <div className="report-right" ref={aiReportRef}>
                    <div className="report-right-header">
                        <h2 className="report-title">✨ 人生深度解析报告</h2>
                        <Button
                            type="primary"
                            onClick={handleExportImage}
                            disabled={generating || Boolean(generationError)}
                            size="large"
                            style={{
                                background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                                border: 'none',
                                boxShadow: '0 6px 16px rgba(212, 175, 106, 0.4)',
                                fontWeight: 600,
                                fontSize: '16px',
                                height: '48px',
                                padding: '0 32px'
                            }}
                        >
                            🖼️ 保存为图片
                        </Button>
                    </div>
                    
                    {/* 保存提示 */}
                    <div style={{
                        margin: '16px 0 24px',
                        padding: '16px 20px',
                        background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.15) 0%, rgba(212, 175, 106, 0.08) 100%)',
                        borderRadius: '12px',
                        border: '2px solid rgba(212, 175, 106, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        boxShadow: '0 4px 12px rgba(212, 175, 106, 0.15)'
                    }}>
                        <div style={{ 
                            fontSize: '32px',
                            flexShrink: 0
                        }}>
                            💡
                        </div>
                        <div style={{ flex: 1 }}>
                            <div style={{ 
                                fontSize: '16px',
                                fontWeight: 600,
                                color: '#d4af6a',
                                marginBottom: '6px'
                            }}>
                                报告内容较长，建议先保存再慢慢品读
                            </div>
                            <div style={{ 
                                fontSize: '14px',
                                color: '#666',
                                lineHeight: '1.6'
                            }}>
                                点击上方"保存为图片"按钮，将报告保存到相册，方便随时查看
                            </div>
                        </div>
                    </div>
                    
                    {hasAiContent ? (
                        <div className="markdown-content" aria-busy={generating}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {aiAnswer}
                            </ReactMarkdown>
                            {generating && <span className="report-stream-cursor" aria-hidden="true" />}
                        </div>
                    ) : (
                        <div style={{
                            margin: '8px 0 20px',
                            padding: '26px 24px',
                            borderRadius: '12px',
                            border: '1px solid rgba(212, 175, 106, 0.3)',
                            background: 'linear-gradient(135deg, rgba(248, 246, 255, 0.85) 0%, rgba(255, 249, 240, 0.85) 100%)'
                        }}>
                            <div style={{
                                fontSize: '18px',
                                fontWeight: 600,
                                color: '#8b6914',
                                marginBottom: '8px'
                            }}>
                                AI 正在组织报告内容
                            </div>
                            <div style={{
                                fontSize: '14px',
                                color: '#666',
                                lineHeight: '1.7'
                            }}>
                                已建立流式连接，正在生成第一段正文，请稍候几秒。
                            </div>
                        </div>
                    )}
                    {generating && (
                        <div className="report-stream-status" role="status">
                            <span className="report-stream-dots" aria-hidden="true">
                                <span /><span /><span />
                            </span>
                            <span>正在生成，内容将持续更新</span>
                        </div>
                    )}
                    {generationError && <p role="alert" style={{ color: '#b42318' }}>{generationError}</p>}


                    {/* 付费墙 */}
                    {/* 暂时注释，不使用解锁功能
                    {isLocked && (
                        <div className="report-paywall" style={{
                            marginTop: '40px',
                            marginBottom: '40px'
                        }}>
                            <div className="paywall-content">
                                <div className="paywall-lock-img"></div>
                                <h3 className="paywall-title">解锁完整报告：深度分析导航</h3>
                                <p className="paywall-desc">
                                    包含基于地理磁场校正的专属能量曲线，建议在做出重大决策前查阅
                                </p>
                                
                                <div style={{ 
                                    fontSize: '13px', 
                                    color: '#999', 
                                    marginBottom: '16px',
                                    padding: '12px',
                                    background: 'rgba(255, 255, 255, 0.5)',
                                    borderRadius: '8px',
                                    lineHeight: '1.6'
                                }}>
                                    📚 <strong>第一章：人格原型识别</strong>（已解锁）<br/>
                                    🔒 <strong>下一章：2026-2030 关键避坑策略</strong>（待解锁）
                                </div>
                                
                                <div className="paywall-price-container">
                                    <span className="price-original">￥59.00</span>
                                    <span className="price-current">
                                        <span className="price-symbol">￥</span>29.9
                                    </span>
                                </div>

                                <div style={{ marginTop: '24px', marginBottom: '16px' }}>
                                    <Input 
                                        placeholder="请输入邀请码" 
                                        value={unlockCode}
                                        onChange={(e) => setUnlockCode(e.target.value.toUpperCase())}
                                        style={{ 
                                            height: '46px', 
                                            marginBottom: '12px', 
                                            fontSize: '15px', 
                                            fontWeight: 600, 
                                            letterSpacing: '2px',
                                            textAlign: 'center',
                                            textTransform: 'uppercase'
                                        }}
                                    />
                                    <Button 
                                        className="unlock-button"
                                        onClick={handleVerifyUnlock}
                                        style={{ width: '100%' }}
                                    >
                                        立即解锁全篇
                                    </Button>
                                </div>
                                
                                <p className="paywall-footer-note">🔑 输入邀请码即可永久解锁 · 只需一杯奶茶钱，换取未来五年的避坑指南</p>
                            </div>
                        </div>
                    )}
                    */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <img src={donateQrUrl} width={400} height={400} alt="赞赏码" />
                    </div>
                    <div style={{
                        marginTop: '14px',
                        padding: '14px 16px',
                        background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.1) 0%, rgba(248, 246, 255, 0.75) 100%)',
                        borderRadius: '10px',
                        border: '1px solid rgba(212, 175, 106, 0.35)',
                        color: '#6b5a3a',
                        fontSize: '14px',
                        lineHeight: '1.9',
                        textAlign: 'center'
                    }}>
                        <div>这个站没有团队、没有融资、没有广告位，只有我一个人硬扛。</div>
                        <div>每次生成报告都在烧 GPU 算力和接口费用，很多时候你看到一行字，我背后是实打实地在倒贴成本。</div>
                        <div>如果这份报告对你有一点点帮助，扫码支持一下，哪怕是一杯矿泉水的钱，都能让我多撑一天。</div>
                    </div>

                    {/* 网站推广卡片 */}
                    <div style={{
                        marginTop: '40px',
                        padding: '24px 20px',
                        background: 'linear-gradient(135deg, #f8f6ff 0%, #fff9f0 100%)',
                        borderRadius: '12px',
                        border: '2px solid #d4af6a',
                        textAlign: 'center',
                        boxShadow: '0 4px 12px rgba(212, 175, 106, 0.15)'
                    }}>
                        <div style={{ fontSize: '24px', marginBottom: '12px' }}>🔗</div>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#6b21a8',
                            marginBottom: '8px'
                        }}>
                            发现更多分析工具
                        </div>
                        <div style={{
                            fontSize: '14px',
                            color: '#666',
                            lineHeight: '1.6',
                            marginBottom: '12px'
                        }}>
                            双人关系分析、年度趋势、关系分析...<br/>
                            访问官网探索完整功能
                        </div>
                        <div style={{
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#d4af6a',
                            letterSpacing: '0.5px',
                            fontFamily: 'monospace'
                        }}>
                            https://www.shuomingshu.dev
                        </div>
                    </div>

                    <div style={{
                        marginTop: '40px',
                        padding: '40px 24px',
                        background: 'linear-gradient(135deg, rgba(212, 175, 106, 0.08) 0%, rgba(212, 175, 106, 0.03) 100%)',
                        borderRadius: '16px',
                        border: '2px solid rgba(212, 175, 106, 0.2)',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '32px', marginBottom: '16px' }}>✨</div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: 600,
                            color: '#333',
                            marginBottom: '12px',
                            lineHeight: '1.6'
                        }}>
                            感谢您阅读完整报告
                        </h3>
                        <p style={{
                            fontSize: '15px',
                            color: '#666',
                            lineHeight: '1.8',
                            marginBottom: '24px',
                            maxWidth: '600px',
                            margin: '0 auto 24px'
                        }}>
                            点击上方“保存为图片”按钮，将这份珍贵的心理原型报告保存到相册，<br />
                            随时回顾人生轨迹，把握关键节点。
                        </p>
                        <Button
                            type="primary"
                            size="large"
                            onClick={handleExportImage}
                            icon={<span style={{ fontSize: '20px' }}>📸</span>}
                            style={{
                                background: 'linear-gradient(135deg, #d4af6a 0%, #b8935d 100%)',
                                border: 'none',
                                height: '56px',
                                padding: '0 48px',
                                fontSize: '17px',
                                fontWeight: 600,
                                boxShadow: '0 6px 20px rgba(212, 175, 106, 0.3)',
                                transition: 'all 0.3s ease'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.05)';
                                e.currentTarget.style.boxShadow = '0 8px 24px rgba(212, 175, 106, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(212, 175, 106, 0.3)';
                            }}
                        >
                            立即保存长图
                        </Button>
                    </div>


                </div>
            </div>
        </div>
    );
};

export default Report;
